import { useState, useEffect } from "react";

type Platform = "android-chrome" | "ios" | "desktop-chrome" | "unsupported";

interface InstallPromptState {
  platform: Platform;
  canInstall: boolean;
  isInstalled: boolean;
  triggerInstall: () => Promise<boolean>;
}

function detectPlatform(): Platform {
  const ua = navigator.userAgent;
  const isIOS = /iphone|ipad|ipod/i.test(ua);
  const isAndroid = /android/i.test(ua);
  const isChrome = /chrome/i.test(ua) && !/edg/i.test(ua);
  const isEdge = /edg/i.test(ua);
  const isSafari = /safari/i.test(ua) && !/chrome/i.test(ua);

  if (isIOS) return "ios";
  if (isAndroid && (isChrome || isEdge)) return "android-chrome";
  if (!isIOS && !isAndroid && (isChrome || isEdge || isSafari)) return "desktop-chrome";
  return "unsupported";
}

function isRunningStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true
  );
}

export function useInstallPrompt(): InstallPromptState {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const platform = detectPlatform();

  useEffect(() => {
    if (isRunningStandalone()) {
      setIsInstalled(true);
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener("beforeinstallprompt", handler);

    window.addEventListener("appinstalled", () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    });

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const triggerInstall = async (): Promise<boolean> => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return outcome === "accepted";
  };

  const canInstall =
    !isInstalled &&
    (platform === "ios" || !!deferredPrompt);

  return { platform, canInstall, isInstalled, triggerInstall };
}
